import { t, Trans } from "@lingui/macro";
import { Check, Plus } from "@phosphor-icons/react";
import { looksLikePlaceholder, type ResumeDraft } from "@reactive-resume/schema";
import { cn } from "@reactive-resume/utils";

export const exampleNote = (email?: string) => {
  const mail = email?.trim() || "alex.chen@example.com";
  return t`I currently work as a Software Engineer at Stripe in San Francisco for 3 years. I run checkout reliability for failed payments, retries, and the TypeScript services behind them, cutting repeat payment failures by about a third last year. On-call for payments one week a month.
Before that I was an Intern at Figma. I built the file browser in React, shipping a share-link flow to production.
I hold a Bachelor's in Computer Science from Stanford University. Graduated 2023.
Location: San Francisco. Email ${mail}. Phone (415) 555-0142.
Skills: TypeScript, React, Ruby, SQL. I also built a Payments Dashboard in React to test webhooks locally.`;
};

export const notesCoverage = (draft: ResumeDraft) => {
  const experience = (draft.experience ?? []).filter((item) => item.company?.trim());
  const education = draft.education ?? [];
  const location = draft.basics?.location?.trim() ?? "";
  const skills = (draft.skills ?? []).some((item) =>
    (item.keywords ?? []).some((keyword) => keyword.trim()),
  );
  const project = (draft.projects ?? []).some((item) => item.name?.trim());
  const realLocation =
    Boolean(location) && !/^remote$/i.test(location) && !looksLikePlaceholder(location);

  return {
    role: experience.length > 0,
    dates: experience.some((item) => item.date?.trim() && !looksLikePlaceholder(item.date)),
    previous: experience.length > 1,
    education: education.some(
      (item) => item.institution?.trim() && !looksLikePlaceholder(item.institution),
    ),
    contact: realLocation || Boolean(draft.basics?.phone?.trim()),
    skills,
    project,
  };
};

export type ChipKey = keyof ReturnType<typeof notesCoverage>;

export const placeholdersIn = (text: string): Array<{ start: number; end: number }> => {
  const matches: Array<{ start: number; end: number }> = [];
  const re = /\[[^\]\n]{1,80}\]/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text))) {
    matches.push({ start: match.index, end: match.index + match[0].length });
  }
  return matches;
};

const OPTIONAL_BLANK = /result with a number/i;

export const blockingPlaceholdersIn = (text: string): Array<{ start: number; end: number }> =>
  placeholdersIn(text).filter(({ start, end }) => !OPTIONAL_BLANK.test(text.slice(start, end)));

export const joinFact = (current: string, snippet: string): string => {
  const trimmed = current.trimEnd();
  if (!trimmed) return snippet;
  if (/^from\s/i.test(snippet) && !/[.!?]$/.test(trimmed)) return `${trimmed} ${snippet}`;
  return /[.!?]$/.test(trimmed) ? `${trimmed} ${snippet}` : `${trimmed}. ${snippet}`;
};

export const FACT_STEM: Record<ChipKey, RegExp> = {
  role: /i currently work as/i,
  dates: /i've been there since|\bfrom\s+.+\s+to\s+present/i,
  previous: /before that|previously i was/i,
  education: /i studied |i have a |i hold a /i,
  contact: /i live in|based in|location\s*:|email |phone /i,
  skills: /the tools i use most are|skills\s*:/i,
  project: /i (?:also )?built/i,
};

const FACT_TOKENS: Record<ChipKey, string[]> = {
  role: ["[title]", "[company]", "[run / ship / lead this]", "[result with a number]"],
  dates: ["[Month Year]"],
  previous: ["[title]", "[company]", "[built / shipped this]", "[result with a number]"],
  education: [
    "[Bachelor's / Master's / bootcamp]",
    "[field]",
    "[college, university, or bootcamp]",
    "[year]",
  ],
  contact: ["[city]", "[email]", "[phone]"],
  skills: ["[languages, software, or methods]"],
  project: ["[project]", "[tools]", "[what it did]"],
};

export const findHintTarget = (
  text: string,
  hint: ChipKey,
): { start: number; end: number } | null => {
  const stemIdx = text.search(FACT_STEM[hint]);
  const from = stemIdx >= 0 ? stemIdx : 0;

  for (const token of FACT_TOKENS[hint]) {
    const idx = text.indexOf(token, from);
    if (idx >= 0) return { start: idx, end: idx + token.length };
  }

  if (stemIdx < 0) {
    for (const token of FACT_TOKENS[hint]) {
      const idx = text.indexOf(token);
      if (idx >= 0) return { start: idx, end: idx + token.length };
    }
  }

  return null;
};

const CHIP_META: Array<{
  key: ChipKey;
  label: () => string;
  stub: () => string;
}> = [
  {
    key: "role",
    label: () => t`Current job`,
    stub: () =>
      t`I currently work as a [title] at [company]. I've been there since [Month Year]. I [run / ship / lead this], [result with a number].`,
  },
  {
    key: "dates",
    label: () => t`Dates`,
    stub: () => t`I've been there since [Month Year].`,
  },
  {
    key: "previous",
    label: () => t`Previous job`,
    stub: () =>
      t`Previously I was a [title] at [company]. I [built / shipped this], [result with a number].`,
  },
  {
    key: "education",
    label: () => t`Education`,
    stub: () =>
      t`I hold a [Bachelor's / Master's / bootcamp] in [field] from [college, university, or bootcamp], [year].`,
  },
  {
    key: "contact",
    label: () => t`Contact`,
    stub: () => t`Location: [city]. Email: [email]. Phone: [phone].`,
  },
  {
    key: "skills",
    label: () => t`Skills`,
    stub: () => t`Skills: [languages, software, or methods].`,
  },
  {
    key: "project",
    label: () => t`A project`,
    stub: () => t`I also built [project] in [tools]. It [what it did].`,
  },
];

type Props = {
  draft: ResumeDraft;
  onInsert?: (text: string, hint: ChipKey) => void;
  onUseExample?: () => void;
};

export const PromptHints = ({ draft, onInsert, onUseExample }: Props) => {
  const coverage = notesCoverage(draft);
  const hasAnything = Object.values(coverage).some(Boolean);

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[13px] font-semibold leading-snug text-foreground">
            {hasAnything
              ? t`Add more if you want, in any order`
              : t`Add whatever you have, in any order`}
          </p>
          <p className="mt-0.5 text-[12px] font-medium leading-snug text-foreground/60">
            {hasAnything ? (
              t`We'll sort it onto the page. Continue to editor whenever you're ready.`
            ) : (
              <Trans>
                Tap a shortcut to drop in a line. Pressing{" "}
                <kbd className="mx-0.5 inline-block translate-y-[-1px] rounded border border-border bg-secondary px-1.5 py-px text-[10px] font-semibold text-foreground/70">
                  Tab
                </kbd>{" "}
                will jump from one highlighted blank to the next.
              </Trans>
            )}
          </p>
        </div>
        {onUseExample ? (
          <button
            type="button"
            onClick={onUseExample}
            className="text-foreground/55 shrink-0 pt-0.5 text-xs font-medium underline-offset-2 hover:text-foreground hover:underline"
          >
            {t`Try an example`}
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {CHIP_META.map((chip) => {
          const done = coverage[chip.key];
          return (
            <button
              key={chip.key}
              type="button"
              aria-pressed={done}
              onClick={() => onInsert?.(chip.stub(), chip.key)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                done
                  ? "border-primary/25 bg-primary/5 text-foreground/70"
                  : "border-border text-foreground/70 hover:border-foreground/40 hover:text-foreground",
              )}
            >
              {done ? (
                <Check size={12} weight="bold" className="text-primary" />
              ) : (
                <Plus size={12} />
              )}
              {chip.label()}
            </button>
          );
        })}
      </div>
    </div>
  );
};
